import { Injectable, Signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs';
import { of } from 'rxjs';
import { environment } from '../../environments/environment';
import type {
  Province,
  Speciality,
  SpecialitiesResponse,
  CustomerClassesResponse,
  UserCountryResponse,
} from '../routes/customers/drafts-tab/customer-create-modal/customer-create-modal.interface';
import { CacheRef, DropdownCacheService } from './dropdown-cache.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerDropdownCacheService {
  private readonly cache = inject(DropdownCacheService);
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  // Country data: the API returns a single-country response; wrap in [] to satisfy CacheRef<T[]>
  private readonly countryRef: CacheRef<UserCountryResponse> =
    this.cache.register<UserCountryResponse>(
      'customer/country-data',
      () =>
        this.http
          .get<UserCountryResponse>(`${this.baseUrl}/countries/me`, {
            params: { returnAll: 'true' },
          })
          .pipe(
            map((res) =>
              res.success && res.data?.country ? [res] : [],
            ),
          ),
    );

  private readonly classesRef: CacheRef<string> =
    this.cache.register<string>(
      'customer/classes',
      () =>
        this.http
          .get<CustomerClassesResponse>(`${this.baseUrl}/customers/classes`, {
            params: { returnAll: 'true' },
          })
          .pipe(
            map((res) => res.data?.classes ?? ['A', 'B', 'C', 'D']),
            catchError(() => of(['A', 'B', 'C', 'D'])),
          ),
    );

  private readonly specialitiesRef: CacheRef<Speciality> =
    this.cache.register<Speciality>(
      'customer/specialities',
      () =>
        this.http
          .get<SpecialitiesResponse>(`${this.baseUrl}/specialities`, {
            params: { returnAll: 'true' },
          })
          .pipe(
            map((res) =>
              (res.data?.specialities ?? []).filter((s) => !s.isDeleted),
            ),
          ),
    );

  // Public read-only signals — same API as before
  readonly countryOptions: Signal<string[]> = computed(() => {
    const name = this.countryRef.data()[0]?.data?.country?.name;
    return name ? [name] : [];
  });

  readonly provincesData: Signal<Province[]> = computed(
    () => this.countryRef.data()[0]?.data?.country?.provinces ?? [],
  );

  readonly provinceOptions: Signal<string[]> = computed(() =>
    this.provincesData().map((p) => p.name),
  );

  readonly classOptions: Signal<string[]> = this.classesRef.data;

  readonly specialitiesData: Signal<Speciality[]> = this.specialitiesRef.data;

  readonly specialityOptions: Signal<string[]> = computed(() =>
    this.specialitiesRef.data().map((s) => s.abbreviation),
  );

  readonly isReady: Signal<boolean> = computed(
    () =>
      this.countryOptions().length > 0 &&
      this.classOptions().length > 0 &&
      this.specialityOptions().length > 0,
  );

  readonly hasAnyError: Signal<boolean> = computed(
    () =>
      this.countryRef.hasError() ||
      this.classesRef.hasError() ||
      this.specialitiesRef.hasError(),
  );

  readonly isLoadingCountries: Signal<boolean> = this.countryRef.isLoading;
  readonly isLoadingClasses: Signal<boolean> = this.classesRef.isLoading;
  readonly isLoadingSpecialities: Signal<boolean> =
    this.specialitiesRef.isLoading;

  loadCountryOptions(): void {
    this.countryRef.load();
  }

  loadClassOptions(): void {
    this.classesRef.load();
  }

  loadSpecialityOptions(): void {
    this.specialitiesRef.load();
  }

  loadAllOptions(): void {
    this.countryRef.load();
    this.classesRef.load();
    this.specialitiesRef.load();
  }

  getCityOptions(provinceName: string): string[] {
    if (!provinceName) return [];
    const province = this.provincesData().find((p) => p.name === provinceName);
    if (province?.cities) {
      return province.cities
        .map((c) => (typeof c === 'string' ? c : c?.name))
        .filter((city): city is string => Boolean(city));
    }
    return [];
  }

  clearCache(): void {
    this.countryRef.invalidate();
    this.classesRef.invalidate();
    this.specialitiesRef.invalidate();
  }
}
